import { apiClient } from './client';
import type { ApiEnvelope } from './types';
import type { Caja, MovimientoCaja } from './appTypes';
import { mapViaje, type BackendViaje } from './storage';

/** Provincia de destino que el backend asigna a los movimientos nuevos (campo de texto libre). */
export const PROVINCIA_POR_DEFECTO = 'Pichincha';

interface BackendMovimientoCaja {
  id: number;
  caja: number;
  viaje: number;
  fecha_creacion: string;
  numero_factura: string;
  nombre_cliente: string;
  telefono_cliente: string;
  destino: string;
  sucursal_entrega: string;
  valor_total_envio: number;
  valor_logistico?: number | null;
  valor_real_pagado: number | null;
  compania_transporte: string;
  compania_transporte_envio?: string;
  provincia_destino?: string;
  vendedor_usuario?: string;
  nombre_logistico: string;
  restante: number;
  conductor_asignado?: string;
}

interface BackendCaja {
  id: number;
  estado: string;
  valor_total_apertura: number;
  valor_base?: number | null;
  sobrante_anterior?: number | null;
  caja_anterior?: number | null;
  total_recaudado: number;
  fecha_apertura: string;
  fecha_cierre: string | null;
  movimientos: BackendMovimientoCaja[];
}

function mapMovimiento(data: BackendMovimientoCaja): MovimientoCaja {
  return {
    id: data.id,
    caja: data.caja,
    viaje_id: data.viaje,
    fecha_creacion: data.fecha_creacion,
    numero_factura: data.numero_factura || '',
    nombre_cliente: data.nombre_cliente || '',
    telefono_cliente: data.telefono_cliente || '',
    destino: data.destino || '',
    sucursal_entrega: data.sucursal_entrega || '',
    valor_total_envio: Number(data.valor_total_envio || 0),
    valor_logistico: Number(data.valor_logistico ?? data.valor_total_envio ?? 0),
    valor_real_pagado: data.valor_real_pagado !== null ? Number(data.valor_real_pagado) : null,
    compania_transporte: data.compania_transporte || '',
    compania_transporte_envio: data.compania_transporte_envio || '',
    provincia_destino: data.provincia_destino ?? PROVINCIA_POR_DEFECTO,
    vendedor_usuario: data.vendedor_usuario || '',
    nombre_logistico: data.nombre_logistico || '',
    restante: Number(data.restante || 0),
    conductor_asignado: data.conductor_asignado,
  };
}

function mapCaja(data: BackendCaja): Caja {
  return {
    id: data.id,
    estado: data.estado,
    valor_total_apertura: Number(data.valor_total_apertura || 0),
    valor_base: data.valor_base !== null && data.valor_base !== undefined ? Number(data.valor_base) : null,
    sobrante_anterior: Number(data.sobrante_anterior || 0),
    caja_anterior: data.caja_anterior ?? null,
    total_recaudado: Number(data.total_recaudado || 0),
    fecha_apertura: data.fecha_apertura,
    fecha_cierre: data.fecha_cierre,
    movimientos: (data.movimientos || []).map(mapMovimiento),
  };
}

import { type PaginatedResult } from './storage';

export function getCajas(page: number = 1, pageSize: number = 10): Promise<PaginatedResult<Caja>> {
  return apiClient
    .get<ApiEnvelope<any>>(`/api/v1/caja/listar/?page=${page}&page_size=${pageSize}`)
    .then(response => {
      const data = response.data;
      if (data && data.items !== undefined) {
        // Formato paginado
        const numPages = data.num_pages || 1;
        const currentPage = data.page || 1;
        return {
          items: data.items.map(mapCaja),
          count: data.count || 0,
          page: currentPage,
          numPages: numPages,
          pageSize: pageSize,
          totalPages: numPages,
          hasNext: currentPage < numPages,
          hasPrevious: currentPage > 1,
        };
      }
      
      // Formato anterior (sin paginar)
      const items = Array.isArray(data) ? data : (data.data ?? []);
      return {
        items: items.map(mapCaja),
        count: items.length,
        page: 1,
        numPages: 1,
        pageSize: items.length || 10,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      };
    });
}

export function getCajaActual(): Promise<Caja> {
  return apiClient
    .get<ApiEnvelope<BackendCaja>>('/api/v1/caja/actual/')
    .then(response => mapCaja(response.data));
}

export interface AperturaSugeridaCaja {
  valor_base: number;
  sobrante_anterior: number;
  caja_anterior_id: number | null;
  valor_sugerido: number;
  caja_abierta_id: number | null;
}

/** Valor sugerido para la nueva caja: valor base + sobrante de la última caja cerrada. */
export function getAperturaSugeridaCaja(): Promise<AperturaSugeridaCaja> {
  return apiClient
    .get<ApiEnvelope<AperturaSugeridaCaja>>('/api/v1/caja/apertura-sugerida/')
    .then(response => ({
      valor_base: Number(response.data.valor_base || 0),
      sobrante_anterior: Number(response.data.sobrante_anterior || 0),
      caja_anterior_id: response.data.caja_anterior_id ?? null,
      valor_sugerido: Number(response.data.valor_sugerido || 0),
      caja_abierta_id: response.data.caja_abierta_id ?? null,
    }));
}

export function abrirCaja(valorTotalApertura: number): Promise<Caja> {
  return apiClient
    .post<ApiEnvelope<BackendCaja>>('/api/v1/caja/abrir/', { valor_total_apertura: valorTotalApertura })
    .then(response => mapCaja(response.data));
}

export function cerrarCaja(): Promise<Caja> {
  return apiClient
    .post<ApiEnvelope<BackendCaja>>('/api/v1/caja/cerrar/', {})
    .then(response => mapCaja(response.data));
}

export function agregarViajeCaja(viajeId: string | number): Promise<Caja> {
  return apiClient
    .post<ApiEnvelope<BackendCaja>>('/api/v1/caja/agregar-viaje/', { viaje_id: Number(viajeId) })
    .then(response => mapCaja(response.data));
}

export function quitarViajeCaja(movimientoId: number): Promise<Caja> {
  return apiClient
    .delete<ApiEnvelope<BackendCaja>>(`/api/v1/caja/quitar-viaje/${movimientoId}/`)
    .then(response => mapCaja(response.data));
}

export function actualizarValorRealCaja(movimientoId: number, valorRealPagado: number, companiaTransporte?: string): Promise<Caja> {
  const body: Record<string, any> = { valor_real_pagado: valorRealPagado };
  if (companiaTransporte !== undefined) {
    body.compania_transporte = companiaTransporte;
  }
  return apiClient
    .put<ApiEnvelope<BackendCaja>>(`/api/v1/caja/actualizar-valor/${movimientoId}/`, body)
    .then(response => mapCaja(response.data));
}

export interface ActualizacionMovimientoCaja {
  valor_real_pagado?: number;
  compania_transporte?: string;
  valor_logistico?: number | null;
  provincia_destino?: string;
}

/** Guarda en la caja los campos editables de un movimiento. */
export function actualizarMovimientoCaja(movimientoId: number, cambios: ActualizacionMovimientoCaja): Promise<Caja> {
  return apiClient
    .put<ApiEnvelope<BackendCaja>>(`/api/v1/caja/actualizar-valor/${movimientoId}/`, cambios)
    .then(response => mapCaja(response.data));
}

/** Descarga el reporte Excel de la caja generado por el backend. */
export async function descargarExcelCaja(cajaId: number): Promise<void> {
  const { getAccessToken, API_BASE_URL } = await import('./client');
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/caja/${cajaId}/exportar/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`No se pudo generar el Excel de la caja (HTTP ${response.status}).`);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `Caja_${cajaId}.xlsx`;
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export interface ViajeDisponibleCaja {
  id: number;
  created_at: string | null;
  sucursal_nombre: string;
  valor_total_envio: number;
  numero_factura: string;
  cliente_nombre: string;
  compania_transporte: string;
  conductor_nombre: string;
  vendedor_usuario?: string;
}

export function getViajesDisponiblesCaja(params?: { page?: number; page_size?: number; search?: string }): Promise<PaginatedResult<ViajeDisponibleCaja>> {
  const queryParams: Record<string, string> = {};
  if (params?.page) queryParams.page = String(params.page);
  if (params?.page_size) queryParams.page_size = String(params.page_size);
  if (params?.search) queryParams.search = params.search;

  return apiClient
    .get<ApiEnvelope<{
      items: ViajeDisponibleCaja[];
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    }>>('/api/v1/caja/viajes-disponibles/', queryParams)
    .then(response => ({
      items: response.data.items,
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      hasNext: response.data.has_next,
      hasPrevious: response.data.has_previous,
    }));
}
